import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { UseMutationOptions, UseMutationResult, useQueryClient } from 'react-query';
import { getOrderDetailsEndpointIdentifier } from './use-get-order-details-api';

const endpoint = 'api/v1/cms/orders';

type TData = void;
type TError = AxiosError;
type TVariables = { orderId: number };

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type TReturnType = UseMutationResult<TData, TError, TVariables>;

// PATCH api/v1/cms/orders/:id/confirm — ops confirm a placed order on the supplier's behalf (204; 409 on a wrong status).
export function useConfirmOrderApi(options?: Options): TReturnType {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, onError: userOnError, ...restOptions } = options || {};

  const mutationFn = async ({ orderId }: TVariables): Promise<TData> => {
    try {
      await http.patch(`${endpoint}/${orderId}/confirm`);
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({ description: error.response.data.message, status: 'error' });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${endpoint}/confirm`],
    onSuccess: (data, variables, context) => {
      toast({ description: 'Order confirmed. The restaurant has been notified.', status: 'success' });
      queryClient.invalidateQueries([getOrderDetailsEndpointIdentifier]);
      userOnSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      // The order moved under us: refetch so the buttons match reality.
      if (error.response?.status === 409) queryClient.invalidateQueries([getOrderDetailsEndpointIdentifier]);
      userOnError?.(error, variables, context);
    },
    ...restOptions,
  });
}
